import React from 'react';
import { Link } from 'react-router-dom';
import { useApps } from '@/contexts/AppsContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate } from '@/lib/utils';
import { Plus, MoreVertical, Pencil, Trash2, Key, ExternalLink, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function AppsListPage() {
  const { apps, deleteApp, selectApp, loading } = useApps();
  const { isAdmin } = useAuth();

  const handleDelete = async (app, e) => {
    e.preventDefault();
    if (window.confirm(`Are you sure you want to delete "${app.name}"? This action cannot be undone.`)) {
      await deleteApp(app.appId);
    }
  };

  const getPlanBadgeVariant = (plan) => {
    switch (plan) {
      case 'pro':
        return 'default';
      case 'starter':
        return 'secondary';
      case 'enterprise':
        return 'success';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            {isAdmin ? 'All Platform Apps' : 'Your Apps'}
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-primary/50 text-primary font-normal">
                <Shield className="h-2.5 w-2.5 mr-0.5" />
                {apps.length} total
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground">
            {isAdmin
              ? 'Manage all TruContext applications on the platform'
              : 'Create and manage your TruContext applications'
            }
          </p>
        </div>
        <Button asChild>
          <Link to="/apps/new">
            <Plus className="mr-2 h-4 w-4" />
            Create App
          </Link>
        </Button>
      </div>

      {/* Apps Grid */}
      {apps.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Plus className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No apps yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first app to start using the TruContext API
            </p>
            <Button asChild>
              <Link to="/apps/new">
                <Plus className="mr-2 h-4 w-4" />
                Create App
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => (
            <Card
              key={app.appId}
              className="hover:border-primary/50 transition-colors"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">
                      <Link
                        to={`/apps/${app.appId}`}
                        onClick={() => selectApp(app)}
                        className="hover:underline"
                      >
                        {app.name}
                      </Link>
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {app.description || 'No description'}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/apps/${app.appId}`} className="flex items-center">
                          <ExternalLink className="mr-2 h-4 w-4" />
                          View Details
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/apps/${app.appId}/edit`} className="flex items-center">
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/keys?app=${app.appId}`} className="flex items-center">
                          <Key className="mr-2 h-4 w-4" />
                          API Keys
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => handleDelete(app, e)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {isAdmin && app.adminEmail && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Owner: {app.adminEmail}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Badge variant={app.status === 'active' ? 'success' : 'secondary'}>
                      {app.status}
                    </Badge>
                    <Badge variant={getPlanBadgeVariant(app.planTier)}>
                      {app.planTier || 'free'}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Created {formatDate(app.createdAt)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
